import React, { useState, useRef, useEffect, useContext } from 'react'
import { useLocation, Redirect } from 'react-router-dom'
import { SchedulerService } from '../../services/Scheduler'
import { Context } from '../../Context'
import { GLOBALS } from '../../utils/Globals'
import { SchedulForm } from '../../components/SchedulerForm'
import { Layout } from '../../components/Layout'
import './styles.scss'
import { PodsService } from '../../services/Pods'
import { Growl } from 'primereact/growl'
import { DateTime } from 'luxon'

export const CreateEditScheduler = () => {
  const useQuery = () => new URLSearchParams(useLocation().search)
  const idlaboratory = Number(useQuery().get('laboratory_id'))

  const { isAuth: token } = useContext(Context)
  const [isLoading, setIsLoading] = useState(false)
  const [, setError] = useState(null)
  const [redirectTo, setRedirectTo] = useState(null)
  const growlRef = useRef(null)

  const [podAvaiblesForReserve, setPodAvaibles] = useState([])
  const [keyHours, setKeyHours] = useState({})
  const [selectedDay, setSelectedDay] = useState(DateTime.local())

  const config = {
    title: 'Reserva para laboratorio',
    icon: GLOBALS.menu.scheduler.icon,
    link: GLOBALS.menu.reserve.linkList,
    linkCourses: GLOBALS.menu.courses.linkList,
  }

  const showGrowl = (severity, summary, detail) => {
    if (growlRef.current) {
      growlRef.current.show({ severity, summary, detail })
    }
  }

  useEffect(() => {
    const fetchLaboratoryData = async () => {
      setIsLoading(true)
      try {
        const podsAvaibles = await PodsService.getFreepods(idlaboratory, token)
        setPodAvaibles(podsAvaibles)

        // Verificar si hay pods disponibles
        if (podsAvaibles.freePods && podsAvaibles.freePods.length > 0) {
          // Obtener día actual o el día seleccionado - Usar hora local para el usuario
          const day = selectedDay || DateTime.local()
          const today = DateTime.local()

          podsAvaibles.freePods.forEach((pod) => {
            const podId = pod.id || pod._id
            const availableHours = []

            // Lógica condicional: en desarrollo usar hour, en producción usar hour + 1
            const currentHour = day.hasSame(today, 'day')
              ? process.env.REACT_APP_IS_DEVELOPMENT === 'true'
                ? today.hour
                : today.hour + 1
              : 0

            // Crear lista de horas desde la hora actual hasta las 23:00
            for (let i = currentHour; i < 24; i++) {
              const hourToCheck = day.set({ hour: i })
              availableHours.push({ label: `${i}:00`, value: hourToCheck })
            }

            // Asignar las horas disponibles a este pod
            setKeyHours((prevKeyHours) => ({
              ...prevKeyHours,
              [podId]: availableHours,
            }))
          })
        } else {
          alert(
            'No hay pods disponibles para realizar una reserva. Comuniquese con su administrador'
          )
          setRedirectTo({
            pathname: config.link,
            state: { success: true },
          })
        }
      } catch (error) {
        console.error('Error fetching laboratory data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (token) {
      fetchLaboratoryData()
    }
  }, [config.link, idlaboratory, token, selectedDay])

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)

      // Verificar si el pod seleccionado está disponible
      const isAvaiblePod = await SchedulerService.getAvaiblePod(
        data.startDate,
        data.endDate,
        data.pod_id,
        token
      )

      if (isAvaiblePod.available) {
        // El pod seleccionado está disponible, crear la reserva
        await SchedulerService.create(data, token)
        setError(null)
        setRedirectTo({
          pathname: config.link,
          state: { success: true },
        })
      } else {
        // El pod seleccionado no está disponible
        // Verificar si hay más pods disponibles en este laboratorio
        if (podAvaiblesForReserve.freePods && podAvaiblesForReserve.freePods.length > 1) {
          // Hay más de un pod disponible en el laboratorio, intentar con otros
          let foundAvailablePod = false

          for (const pod of podAvaiblesForReserve.freePods) {
            if (pod.id !== data.pod_id) {
              const alternativePodAvailable = await SchedulerService.getAvaiblePod(
                data.startDate,
                data.endDate,
                pod.id,
                token
              )

              if (alternativePodAvailable.available) {
                // Encontramos un pod alternativo disponible
                const updatedData = { ...data, pod_id: pod.id }
                await SchedulerService.create(updatedData, token)
                setError(null)
                setRedirectTo({
                  pathname: config.link,
                  state: { success: true },
                })
                foundAvailablePod = true
                break // Salir del bucle después de encontrar un pod disponible
              }
            }
          }

          // Si no encontramos ningún pod disponible
          if (!foundAvailablePod) {
            setIsLoading(false)
            showGrowl(
              'error',
              'Ups!',
              'No hay pods disponibles en este horario. Intenta en otro momento.'
            )
          }
        } else {
          // Solo hay un pod y no está disponible
          setIsLoading(false)
          showGrowl(
            'error',
            'Ups!',
            'El pod ya está reservado en este horario. Intenta en otro momento.'
          )
        }
      }
    } catch (e) {
      setIsLoading(false)
      const error = e
      showGrowl('error', 'Ups!', `${error}`)
    }
  }

  if (redirectTo) {
    return <Redirect to={redirectTo} />
  }

  return (
    <Layout>
      <Growl ref={growlRef} />
      {isLoading ? (
        <div className="loading-container">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
        </div>
      ) : (
        <div className="p-fluid">
          <div className="p-grid">
            <div className="p-col-12">
              <div className="card card-w-title">
                <h1 className="create-edit-title">
                  {config.icon && <i className={config.icon}></i>}
                  {config.title}
                </h1>

                <SchedulForm
                  isLoading={isLoading}
                  idlaboratory={idlaboratory}
                  onSubmit={onSubmit}
                  cancelLink={config.link}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  keyHours={keyHours}
                  setKeyHours={setKeyHours}
                  podAvaiblesForReserve={podAvaiblesForReserve}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
